import * as React from 'react';

import { useApolloClient } from '@apollo/client';

import SearchRolesByNameQuery from 'bundles/onboarding-common/queries/SearchRolesByName.graphql';
import type {
  SearchRolesByNameQuery as SearchRolesByNameQueryType,
  SearchRolesByNameQueryVariables,
} from 'bundles/onboarding-common/queries/__generated__/SearchRolesByName';
import type { AvailableRole } from 'bundles/personalization-settings/hooks/useDesiredRolesEditor';

const normalize = (name: string) => name.trim().toLowerCase();

/**
 * Returns a function that looks up an exact (case/whitespace-insensitive)
 * name match in the desired-roles catalog (`PathwaySkillCareerRoles`) --
 * a different catalog/id-namespace than the current-role catalog
 * (`ProfileOccupationChoices`), so a current role's own id can't be reused
 * as a desired-role id. Used to build a `DEFAULT` `SelectedRole` when adding
 * the current role to desired roles; callers should fall back to a `CUSTOM`
 * role when this resolves to `null`.
 */
const useFindRoleInRolesCatalogByName = () => {
  const client = useApolloClient();

  return React.useCallback(
    async (name: string): Promise<AvailableRole | null> => {
      const { data } = await client.query<SearchRolesByNameQueryType, SearchRolesByNameQueryVariables>({
        query: SearchRolesByNameQuery,
        variables: { searchTerm: name.trim() },
        context: { clientName: 'gatewayGql' },
        fetchPolicy: 'network-only',
      });

      const elements = data?.PathwaySkillCareerRoles?.searchRolesByName?.elements ?? [];
      const match = elements.find((role) => role.id && role.name && normalize(role.name) === normalize(name));
      return match?.id && match?.name ? { id: match.id, name: match.name } : null;
    },
    [client]
  );
};

export default useFindRoleInRolesCatalogByName;
